import {ContainerIcon, PackageIcon, PlayIcon} from '@primer/octicons-react'
import {SkeletonText} from '@primer/react/experimental'
import {useClickAnalytics} from '@github-ui/use-analytics'
import type {SidebarPackagesProps} from '../types'
import {SidebarSection} from './SidebarSection'
import {SidebarLink} from './SidebarLink'
import {SidebarListItem} from './SidebarListItem'
import styles from './SidebarPackages.module.css'
import dockerIcon from '../assets/package-docker.svg'
import mavenIcon from '../assets/package-maven.svg'
import npmIcon from '../assets/package-npm.svg'
import nugetIcon from '../assets/package-nuget.svg'
import rubygemsIcon from '../assets/package-rubygems.svg'

// Branded ecosystem icons, keyed by package type. Mirrors the Rails
// Packages::IconComponent SUPPORTED_ICONS list (minus container, which uses an octicon below).
const brandedPackageIcons: Record<string, string> = {
  docker: dockerIcon,
  maven: mavenIcon,
  npm: npmIcon,
  nuget: nugetIcon,
  rubygems: rubygemsIcon,
}

function getPackageIcon(packageType: string, iconUrl?: string | null) {
  if (iconUrl) return <img src={iconUrl} width={16} height={16} alt={packageType} className={styles.packageTypeIcon} />
  if (packageType === 'container') return <ContainerIcon />
  if (packageType === 'actions') return <PlayIcon />

  const brandedIcon = brandedPackageIcons[packageType.toLowerCase()]
  if (brandedIcon) {
    return <img src={brandedIcon} width={16} height={16} alt={packageType} className={styles.packageTypeIcon} />
  }

  return <PackageIcon />
}

/** Renders the Packages section of the repository sidebar. */
export function SidebarPackages({packageCount, packages, packagesPath, canWrite, publishPath}: SidebarPackagesProps) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const moreCount = packageCount - packages.length

  return (
    <SidebarSection
      title="Packages"
      href={packagesPath}
      count={packageCount}
      moreCount={moreCount > 1 ? moreCount : undefined}
      moreLabel={`package${moreCount === 1 ? '' : 's'}`}
    >
      {packages.length > 0 ? (
        <ul className={styles.packageList}>
          {packages.map(pkg => (
            <SidebarListItem
              key={`pkg-${pkg.name}`}
              className={styles.packageItem}
              icon={getPackageIcon(pkg.packageType, pkg.iconUrl)}
              label={pkg.name}
              href={pkg.path}
              onClick={() =>
                sendClickAnalyticsEvent({
                  category: 'REPOSITORY_OVERVIEW',
                  action: 'click',
                  label: 'location:sidebar;section:packages',
                })
              }
            />
          ))}
        </ul>
      ) : (
        <div className={styles.emptyState}>
          <span>No packages published</span>
          {canWrite && publishPath && (
            <>
              <br />
              <SidebarLink to={publishPath} inline>
                <span>Publish your first package</span>
              </SidebarLink>
            </>
          )}
        </div>
      )}
    </SidebarSection>
  )
}

export function SidebarPackagesSkeleton() {
  return (
    <SidebarSection title="Packages">
      <SkeletonText />
    </SidebarSection>
  )
}
